import React, { useEffect, useRef } from 'react';
import { Box, Flex, Pane, Text } from '@fullstory/components';
import { shortNumber } from '@fullstory/util/fmt';
import { getPercentString } from '../../util/fmt';
import {
  BOX_LEFT_RIGHT_MARGIN,
  BOX_WIDTH,
  LINK_LENGTH_MINUS_MARGIN,
  MIN_INTERACTIONS,
} from './const';
import css from './index.module.scss';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { useIntl } from 'react-intl';

interface OrderNumberProps extends Pick<React.ComponentProps<typeof Box>, 'marginTop'> {
  value: number;
}

export const OrderNumber = React.forwardRef<HTMLDivElement, OrderNumberProps>(
  ({ value, marginTop = 0 }, ref) => (
    <>
      {/* Hack: add a scroll-margin-top so that the controls above the JourneyMap remain in view */}
      <Box ref={ref} style={{ scrollMarginTop: '50px' }} />
      <Box marginTop={marginTop}>
        <Pane
          className={css.orderedNumber}
          backgroundColor="secondary"
          height={24}
          width={24}
          minWidth={24}
          marginRight={8}
        >
          <Text appearance="bodyMedium">{value}</Text>
        </Pane>
      </Box>
    </>
  ),
);

OrderNumber.displayName = 'OrderNumber';

interface JourneyMapHeaderProps {
  interactionSession: {
    x: number;
    continue: number;
    exit: number;
    total: number;
  };
  index: number;
  direction: JourneyMapSettings.Direction;
  inSameSession: boolean;
  totalInteractions: number;
  maxDepth: number;
}
export const JourneyMapHeader: React.FC<JourneyMapHeaderProps> = ({
  interactionSession,
  index,
  direction,
  inSameSession,
  totalInteractions,
  maxDepth,
}) => {
  const isForward = direction == JourneyMapSettings.Direction.DIRECTION_FORWARD;
  const intl = useIntl();
  const isLastDirectionBackward = totalInteractions === index && !isForward;
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    nodeRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [nodeRef]);

  return (
    <>
      <foreignObject x={interactionSession.x - 12 + BOX_WIDTH / 2} y={0} width={25} height={60}>
        <OrderNumber
          value={isForward ? index : maxDepth - index + 2}
          marginTop={24}
          ref={isLastDirectionBackward && maxDepth + 1 === MIN_INTERACTIONS ? nodeRef : null}
        />
      </foreignObject>
      {index <= maxDepth && (
        <>
          {!(!isForward && index === 1) && (
            <foreignObject
              x={
                (isForward ? interactionSession.x + BOX_WIDTH : interactionSession.x - BOX_WIDTH) +
                BOX_LEFT_RIGHT_MARGIN / 2
              }
              y={24}
              width={LINK_LENGTH_MINUS_MARGIN}
              height={48}
            >
              <TotalSessionStats
                inSameSession={inSameSession}
                title={
                  isForward
                    ? intl.formatMessage({ defaultMessage: 'Total continue', id: 'Am7Ri7' })
                    : intl.formatMessage({ defaultMessage: 'Total previous', id: 'rElGyQ' })
                }
                session={interactionSession.continue}
                totalInteractionSession={interactionSession.total}
              />
              <TotalSessionStats
                inSameSession={inSameSession}
                title={
                  isForward
                    ? intl.formatMessage({ defaultMessage: 'Total exit', id: 'Y8Yl79' })
                    : intl.formatMessage({ defaultMessage: 'Total entry', id: 'H3OlYi' })
                }
                session={interactionSession.exit}
                totalInteractionSession={interactionSession.total}
              />
            </foreignObject>
          )}
        </>
      )}
    </>
  );
};

interface TotalSessionStatsProps {
  title: string;
  session: number;
  totalInteractionSession: number;
  inSameSession: boolean;
}
export function TotalSessionStats({
  title,
  session,
  totalInteractionSession,
  inSameSession,
}: TotalSessionStatsProps) {
  const intl = useIntl();
  return (
    <Flex data-component="TotalSessionStats" testId="header-data" className="fs-mask">
      <Box width={107}>
        <Text align="left" appearance="bodyXSmall">
          {title}
        </Text>
      </Box>
      <Box width={60}>
        <Text align="right" appearance="bodyXSmallBold">
          {getPercentString(session, totalInteractionSession)}
        </Text>
      </Box>
      <Box width={124}>
        <Text align="right" appearance="bodyXSmallBold">
          {shortNumber(session)}{' '}
          {inSameSession
            ? intl.formatMessage({ defaultMessage: 'Sessions', id: 'Z3EDd9' })
            : intl.formatMessage({ defaultMessage: 'Users', id: 'YDMrKK' })}
        </Text>
      </Box>
    </Flex>
  );
}
